import React, { useEffect, useState } from "react";
import api from "../services/api";
import { Link } from "react-router-dom";
import {
  FaHeart,
  FaTrash,
  FaPlay,
  FaSpinner,
  FaExclamationTriangle,
  FaHeartBroken,
  FaBroom
} from "react-icons/fa";

function Favorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState({});
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        setLoading(true);
        const res = await api.get("/user/favorites");
        setFavorites(res.data.favorites);
      } catch (err) {
        console.error("Error fetching favorites");
        setError("Failed to load favorites");
      } finally {
        setLoading(false);
      }
    };

    fetchFavorites();
  }, []);

  const handleRemove = async (movieId) => {
    setRemoving(prev => ({ ...prev, [movieId]: true }));
    try {
      const res = await api.delete(`/user/favorites/${movieId}`);
      setFavorites(res.data.favorites);
    } catch (err) {
      console.error("Error removing favorite");
      setError("Failed to remove favorite");
    } finally {
      setRemoving(prev => ({ ...prev, [movieId]: false }));
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Are you sure you want to clear all favorites? This action cannot be undone.")) {
      return;
    }

    setClearing(true);
    try {
      await api.delete("/user/favorites/clear");
      setFavorites([]);
    } catch (err) {
      console.error("Error clearing favorites");
      setError("Failed to clear favorites");
    } finally {
      setClearing(false);
    }
  };

  const MovieCard = ({ movie, index }) => (
    <div className="group relative bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
      <Link to={`/movie/${movie.movieId}`} className="block">
        <div className="relative overflow-hidden">
          {movie.posterPath ? (
            <img
              src={`https://image.tmdb.org/t/p/w500${movie.posterPath}`}
              alt={movie.title}
              className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-80 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <FaPlay className="text-4xl mx-auto mb-2" />
                <span className="text-sm">No Image Available</span>
              </div>
            </div>
          )}

          {/* Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <FaPlay className="text-white text-3xl bg-white/20 rounded-full p-3 backdrop-blur-sm" />
            </div>
          </div>

          {/* Heart Badge */}
          <div className="absolute top-3 left-3 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
            <FaHeart className="text-xs" />
            <span>Favorite</span>
          </div>
        </div>

        <div className="p-4">
          <h3 className="font-bold text-gray-900 text-lg mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
            {movie.title}
          </h3>
        </div>
      </Link>

      {/* Remove Button */}
      <div className="absolute bottom-4 right-4">
        <button
          onClick={(e) => {
            e.preventDefault();
            handleRemove(movie.movieId);
          }}
          disabled={removing[movie.movieId]}
          className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {removing[movie.movieId] ? (
            <FaSpinner className="animate-spin text-sm" />
          ) : (
            <FaTrash className="text-sm" />
          )}
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-4xl text-red-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your favorites...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-red-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-500 via-pink-500 to-red-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 flex items-center justify-center space-x-4">
            <FaHeart className="text-red-200" />
            <span>My Favorites</span>
          </h1>
          <p className="text-xl md:text-2xl text-white/90">
            {favorites.length > 0
              ? `You have ${favorites.length} favorite movie${favorites.length !== 1 ? 's' : ''}`
              : "Your favorite movies will appear here"
            }
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6 flex items-center space-x-2">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {favorites.length > 0 && (
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Your Favorite Movies
              </h2>
              <p className="text-gray-600">
                {favorites.length} movie{favorites.length !== 1 ? 's' : ''} in your collection
              </p>
            </div>

            <button
              onClick={handleClear}
              disabled={clearing}
              className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {clearing ? (
                <FaSpinner className="animate-spin" />
              ) : (
                <FaBroom />
              )}
              <span>Clear All</span>
            </button>
          </div>
        )}

        {favorites.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((movie, index) => (
              <MovieCard key={movie.movieId} movie={movie} index={index} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-6xl mb-4">💔</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              No Favorites Yet
            </h3>
            <p className="text-gray-600 mb-8">
              Start building your collection by adding movies to your favorites
            </p>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 bg-red-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-red-600 transition-colors"
            >
              <FaHeart />
              <span>Discover Movies</span>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Favorites;